import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { GradeBadge, StatusChip } from "@/components/Badges";
import { ArrowLeft, User, MapPin } from "lucide-react";

export default function WeaverProfile() {
  const { weaverId } = useParams();
  const { t } = useLang();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/weavers/${weaverId}/profile`).then((r) => setData(r.data)); }, [weaverId]);
  if (!data) return <div>{t("loading")}</div>;
  const { weaver, active_pos, looms, grade_history, on_time_pct, pieces_delivered, pieces_outstanding } = data;
  return (
    <div>
      <button onClick={() => nav(-1)} className="text-sm flex items-center gap-1 mb-3 text-[#4A627A]"><ArrowLeft size={16}/> Back</button>
      <div className="hag-surface p-5">
        <div className="flex items-start gap-3 flex-wrap">
          <div className="w-14 h-14 rounded-md flex items-center justify-center" style={{ background: "var(--brand-navy)", color: "var(--brand-gold)" }}>
            <User size={24} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="eyebrow">Weaver</div>
            <h1 className="text-2xl font-bold" style={{ color: "var(--brand-navy)" }}>{weaver.name}</h1>
            <div className="text-sm text-[#4A627A] flex items-center gap-1 mt-1"><MapPin size={14}/> {weaver.village || "—"} · Zone {weaver.zone || "?"}</div>
            {weaver.phone && <div className="text-xs text-[#7C8E9E] mt-1">{weaver.phone}</div>}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
        <Stat label="Active POs" v={active_pos.length} />
        <Stat label="Looms" v={looms.length} />
        <Stat label="On-time %" v={on_time_pct === null ? "—" : `${on_time_pct}%`} />
        <Stat label="Outstanding pcs" v={pieces_outstanding} />
      </div>

      <h2 className="mt-6 font-bold" style={{ color: "var(--brand-navy)" }}>Active POs</h2>
      <div className="mt-2 space-y-2">
        {active_pos.map((p) => (
          <Link key={p.id} to={`/po/${p.id}`} data-testid={`wp-po-${p.po_number}`} className="hag-surface p-3 flex items-center gap-2 flex-wrap">
            <span className="font-semibold" style={{ color: "var(--brand-navy)" }}>{p.po_number}</span>
            <StatusChip status={p.status} />
            <span className="text-xs text-[#7C8E9E]">ETA {p.eta ? new Date(p.eta).toLocaleDateString() : "—"}</span>
          </Link>
        ))}
        {active_pos.length === 0 && <div className="text-sm text-[#7C8E9E]">No active POs.</div>}
      </div>

      <h2 className="mt-6 font-bold" style={{ color: "var(--brand-navy)" }}>Grade history (closed POs)</h2>
      <div className="mt-2 space-y-1">
        {grade_history.length === 0 && <div className="text-sm text-[#7C8E9E]">No closed POs yet.</div>}
        {grade_history.map((g, i) => (
          <div key={i} data-testid={`wp-grade-${i}`} className="hag-surface p-2 flex items-center gap-2 flex-wrap">
            <GradeBadge grade={g.grade} />
            <span className="font-medium text-sm">{g.po_number}</span>
            {g.closure_date && <span className="text-xs text-[#7C8E9E]">Closed {new Date(g.closure_date).toLocaleDateString()}</span>}
          </div>
        ))}
      </div>

      <div className="mt-4 text-xs text-[#7C8E9E]">Pieces delivered lifetime: <b>{pieces_delivered}</b></div>
    </div>
  );
}

function Stat({ label, v }) {
  return (
    <div className="hag-surface p-3">
      <div className="text-[10px] uppercase tracking-widest text-[#7C8E9E]">{label}</div>
      <div className="text-2xl font-black mt-1" style={{ color: "var(--brand-navy)" }}>{v}</div>
    </div>
  );
}
